import { useEffect, useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import { Checkbox } from 'src/components/ui/checkbox';
import { useCreateContact } from 'src/api/core/communications/contacts/useCreateContact';
import { useUpdateContact } from 'src/api/core/communications/contacts/useUpdateContact';

export interface ContactFormInitialValues {
  id?: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  title?: string | null;
  isPrimary?: boolean;
  metadata?: object | null;
}

interface ContactFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** When provided, the dialog is in edit mode and uses these values as defaults. */
  initialValues?: ContactFormInitialValues | null;
  /** Locked company id used for create. Required even in edit mode for refetching/cache invalidation. */
  companyId: string;
  /** Whether the current user can edit the metadata JSON field. */
  isAdmin?: boolean;
  onSuccess?: () => void;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  title: string;
  isPrimary: boolean;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  metadata?: string;
}

const phoneRegex =
  /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}[-\s.]?[0-9]{1,9}$/;

function isValidEmail(email: string): boolean {
  if (!email || email.trim() === '') return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidPhone(phone: string): boolean {
  if (!phone || phone.trim() === '') return true;
  return phoneRegex.test(phone);
}

const EMPTY_FORM: FormData = {
  name: '',
  email: '',
  phone: '',
  title: '',
  isPrimary: false,
};

export function ContactFormDialog({
  open,
  onClose,
  initialValues,
  companyId,
  isAdmin = false,
  onSuccess,
}: ContactFormDialogProps) {
  const gt = useGT();
  const isEdit = !!initialValues?.id;
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [metadataText, setMetadataText] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  // Sync form state from props each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setFormData({
      name: initialValues?.name ?? '',
      email: initialValues?.email ?? '',
      phone: initialValues?.phone ?? '',
      title: initialValues?.title ?? '',
      isPrimary: initialValues?.isPrimary ?? false,
    });
    setMetadataText(
      initialValues?.metadata
        ? JSON.stringify(initialValues.metadata, null, 2)
        : '',
    );
    setErrors({});
  }, [open, initialValues]);

  const createContact = useCreateContact();
  const updateContact = useUpdateContact(initialValues?.id ?? '', () => {
    onSuccess?.();
    onClose();
  });

  const isSaving = createContact.isLoading || updateContact.isLoading;

  function validate(): boolean {
    const next: FormErrors = {};
    if (!formData.name.trim()) next.name = gt('Name is required');
    if (formData.email && !isValidEmail(formData.email))
      next.email = gt('Invalid email format');
    if (formData.phone && !isValidPhone(formData.phone))
      next.phone = gt('Invalid phone number format');
    if (isAdmin && metadataText.trim()) {
      try {
        JSON.parse(metadataText);
      } catch {
        next.metadata = gt('Invalid JSON format');
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSave() {
    if (!validate()) return;

    let parsedMetadata: object | null = null;
    if (isAdmin) {
      if (metadataText.trim()) {
        try {
          parsedMetadata = JSON.parse(metadataText);
        } catch {
          return;
        }
      }
    }

    const payload = {
      name: formData.name,
      email: formData.email || undefined,
      phone: formData.phone || undefined,
      title: formData.title || undefined,
      isPrimary: formData.isPrimary,
      ...(isAdmin && { metadata: parsedMetadata }),
    };

    if (isEdit) {
      updateContact.mutate(payload);
    } else {
      createContact.mutate(
        { companyId, ...payload },
        {
          onSuccess: () => {
            onSuccess?.();
            onClose();
          },
        },
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? gt('Edit Contact', {
                  context: 'dialog title for editing an existing contact',
                })
              : gt('Add New Contact', {
                  context: 'dialog title for adding a new contact',
                })}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? gt('Update the contact information.')
              : gt('Add a new contact for this company.')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="contact-name">
              <T>Name *</T>
            </Label>
            <Input
              id="contact-name"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (errors.name) setErrors({ ...errors, name: undefined });
              }}
              placeholder={gt('Enter contact name')}
              required
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact-email">
              <T>Email</T>
            </Label>
            <Input
              id="contact-email"
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (errors.email) setErrors({ ...errors, email: undefined });
              }}
              onBlur={() => {
                if (formData.email && !isValidEmail(formData.email)) {
                  setErrors({ ...errors, email: gt('Invalid email format') });
                }
              }}
              placeholder={gt('Enter email address')}
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact-phone">
              <T>Phone</T>
            </Label>
            <Input
              id="contact-phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => {
                setFormData({ ...formData, phone: e.target.value });
                if (errors.phone) setErrors({ ...errors, phone: undefined });
              }}
              onBlur={() => {
                if (formData.phone && !isValidPhone(formData.phone)) {
                  setErrors({
                    ...errors,
                    phone: gt('Invalid phone number format'),
                  });
                }
              }}
              placeholder={gt('Enter phone number (e.g., +1-555-123-4567)')}
              className={errors.phone ? 'border-red-500' : ''}
            />
            {errors.phone && (
              <p className="text-sm text-red-500">{errors.phone}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact-title">
              <T>Title</T>
            </Label>
            <Input
              id="contact-title"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder={gt('Enter job title')}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="contact-isPrimary"
              checked={formData.isPrimary}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, isPrimary: checked === true })
              }
            />
            <Label
              htmlFor="contact-isPrimary"
              className="text-sm font-medium leading-none"
            >
              <T>Set as primary contact</T>
            </Label>
          </div>
          {isAdmin && (
            <div className="space-y-2">
              <Label htmlFor="contact-metadata">
                <T>Metadata (JSON)</T>
              </Label>
              <Textarea
                id="contact-metadata"
                value={metadataText}
                onChange={(e) => {
                  setMetadataText(e.target.value);
                  if (errors.metadata)
                    setErrors({ ...errors, metadata: undefined });
                }}
                onBlur={() => {
                  if (metadataText.trim()) {
                    try {
                      JSON.parse(metadataText);
                    } catch {
                      setErrors({
                        ...errors,
                        metadata: gt('Invalid JSON format'),
                      });
                    }
                  }
                }}
                placeholder={gt('Enter JSON metadata', {
                  context: 'placeholder for metadata JSON textarea',
                })}
                className="min-h-[120px] font-mono text-sm"
              />
              {errors.metadata && (
                <p className="text-sm text-red-500">{errors.metadata}</p>
              )}
              <p className="text-xs text-gray-500">
                <T>Enter valid JSON for additional contact metadata</T>
              </p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>
            <T context="button label to cancel and close the dialog">Cancel</T>
          </Button>
          <Button onClick={handleSave} disabled={!formData.name || isSaving}>
            {isSaving
              ? gt('Saving...', {
                  context: 'button label while contact is being saved',
                })
              : isEdit
                ? gt('Update Contact', {
                    context:
                      'button label to save edits to an existing contact',
                  })
                : gt('Add Contact', {
                    context: 'button label to create a new contact',
                  })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ContactFormDialog;
